import React, { useState, useEffect } from 'react'
import { FEATURE_GROUPS, PLANS, Plan } from '../lib/types/pricing-table.config'
import { Button } from './Button'
import { PlanId } from 'util/prices'
import { PricingCadenceSelector } from './PricingCadenceSelector'
import { HelperIcon } from './HelperIcon'
import { classNames } from 'util/classNames'
import { IconChevronDown } from '@tabler/icons-react'
import { ModalV2 } from './Common/ModalV2'

type MobilePricingTableProps = {
  condensed?: boolean
  currentPlan?: PlanId | null
  onManageBilling?: () => void
  onSelectPlan?: ({ id, name }: { id: string; name: string }) => void
  isLoadingBillingSession?: boolean
  show?: 'all' | 'heading' | 'comparison'
  href?: string
  isTrialing?: boolean
  setShowUpgradeTrialModal?: (show: boolean) => void
}

export const MobilePricingTable = ({
  condensed = false,
  currentPlan = null,
  onManageBilling = () => {},
  isLoadingBillingSession = false,
  show = 'all',
  href = '',
  onSelectPlan,
  isTrialing = false,
  setShowUpgradeTrialModal = () => {},
}: MobilePricingTableProps) => {
  const [showModal, setShowModal] = useState(false)
  const [billingCycle, setBillingCycle] = useState<'Monthly' | 'Annual'>(
    currentPlan?.includes('Yearly')
      ? 'Annual'
      : !!currentPlan
      ? 'Monthly'
      : 'Monthly'
  )
  const [isLoadingPlanSelect, setIsLoadingPlanSelect] = useState<PlanId | null>(
    null
  )
  const handleSelectPlan = (plan: { id: PlanId; name: string }) => {
    if (onSelectPlan) {
      onSelectPlan(plan)
      return
    }
    setIsLoadingPlanSelect(plan.id)
    if (currentPlan === plan.id && onManageBilling) {
      onManageBilling()
    } else {
      const link = `${
        process.env.NEXT_PUBLIC_BASE_URL
      }/purchase?plan=${encodeURIComponent(
        plan.id
      )}&replaceHistoryPage=${encodeURIComponent(window.location.pathname)}`
      window.open(link, '_self')
    }
    setTimeout(() => {
      setIsLoadingPlanSelect(null)
    }, 4000)
  }

  const filteredPlans = PLANS.filter(
    (plan) => plan.billingCycle === billingCycle
  )

  return (
    <>
      <div className="w-full">
        <div className="mb-4 flex justify-end">
          <PricingCadenceSelector
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
          />
        </div>

        <div className="flex flex-col gap-2">
          {/* Plan Headers - Sticky */}
          <div className="sticky top-0 z-50 grid grid-cols-3 gap-2 bg-white/95 pb-4 pt-2 shadow-sm">
            {filteredPlans.map((plan, idx) => (
              <div
                key={idx}
                className={classNames(
                  'relative rounded-lg border p-2',
                  currentPlan === plan.id
                    ? 'border-toasty-blue-600'
                    : 'border-gray-200'
                )}
              >
                <div className="flex flex-col">
                  <h3 className="text-center text-2xl font-semibold text-gray-900">
                    {plan.name}
                  </h3>
                  <div className="mb-3 flex flex-col items-center">
                    {plan.originalPrice && (
                      <span className="text-xs text-gray-400 line-through">
                        {plan.originalPrice}
                      </span>
                    )}
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-lg font-semibold text-green-600">
                        {plan.price}
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="block whitespace-nowrap text-[10px] text-gray-500">
                          /mo
                        </span>
                      </div>
                    </div>
                    {plan.billingCycle === 'Annual' && (
                      <span className="mt-1 block text-[10px] text-gray-500">
                        billed yearly
                      </span>
                    )}
                  </div>
                  <Button
                    className={classNames(
                      '!h-6 w-full !py-0 !text-xs',
                      currentPlan === plan.id
                        ? '!bg-toasty-blue-600'
                        : '!bg-slate-800'
                    )}
                    onClick={() =>
                      isTrialing && currentPlan === plan.id
                        ? setShowUpgradeTrialModal(true)
                        : handleSelectPlan({ id: plan.id, name: plan.name })
                    }
                    loading={
                      (isLoadingBillingSession && currentPlan === plan.id) ||
                      (isLoadingPlanSelect === plan.id &&
                        isLoadingPlanSelect !== null)
                    }
                  >
                    {isTrialing && currentPlan === plan.id
                      ? 'Upgrade'
                      : currentPlan === plan.id
                      ? 'Manage'
                      : 'Select'}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {condensed && (
            <div
              className="cursor-pointer rounded-lg bg-slate-100"
              onClick={(e) => {
                if (href) {
                  e.preventDefault()
                } else {
                  setShowModal(true)
                }
              }}
            >
              <a
                href={href || undefined}
                className="flex w-full items-center justify-center px-4 py-2 text-center text-xs font-bold text-slate-500 hover:underline"
              >
                Compare plans{' '}
                <IconChevronDown className="inline h-4 w-4 text-slate-500" />
              </a>
            </div>
          )}

          {/* Feature Groups */}
          {!condensed && (show === 'comparison' || show === 'all') && (
            <div>
              {FEATURE_GROUPS.map((group, groupIdx) => (
                <div key={groupIdx} className="mb-6">
                  {group.groupName.trim() !== '' && (
                    <h4 className="mb-4 text-base font-semibold text-gray-900">
                      {group.groupName}
                    </h4>
                  )}
                  <div className="space-y-4">
                    {group.features.map((feature, featureIdx) => (
                      <div
                        key={featureIdx}
                        className="rounded-lg bg-gray-50 p-4"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {feature.name}
                          </span>
                          {feature.tooltip && (
                            <HelperIcon
                              className="h-4 w-4 text-gray-500"
                              id={feature.tooltip}
                              helper={feature.tooltip}
                            />
                          )}
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-4">
                          {filteredPlans.map((plan, planIdx) => (
                            <div
                              key={planIdx}
                              className="text-center text-sm text-gray-600"
                            >
                              {typeof feature.values[plan.id] === 'boolean' ? (
                                feature.values[plan.id] ? (
                                  <span className="text-green-600">✓</span>
                                ) : (
                                  <span className="text-red-600">✗</span>
                                )
                              ) : (
                                feature.values[plan.id]
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {condensed && (
        <ModalV2
          className="md:max-w-5xl"
          open={showModal}
          onClose={() => setShowModal(false)}
        >
          <MobilePricingTable
            condensed={false}
            currentPlan={currentPlan}
            onManageBilling={onManageBilling}
            isLoadingBillingSession={isLoadingBillingSession}
            show="all"
            href={href}
            onSelectPlan={onSelectPlan}
            isTrialing={isTrialing}
            setShowUpgradeTrialModal={setShowUpgradeTrialModal}
          />
        </ModalV2>
      )}
    </>
  )
}
